import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, of } from 'rxjs';
import { DirectivesModule } from 'src/app/directives/directives.modules';
import { UploadFormModule } from '../upload-form/upload-form.module';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { FileEntity } from 'src/app/models/entities/file.entity';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUpload } from 'src/app/models/file-upload.model';

@Component({
    selector: 'app-attachments',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatTooltipModule, DirectivesModule, UploadFormModule],
    templateUrl: './attachments.component.html',
    styleUrl: './attachments.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AttachmentsComponent),
            multi: true
        }
    ]
})
export class AttachmentsComponent implements ControlValueAccessor {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input() attachments: any[] = [];
  @Input() boardName: any[] = [];
  @Input() uploadFolder: string = '';

  @Output() fileAdded: EventEmitter<string> = new EventEmitter<string>();
  @Output() changed: EventEmitter<FileEntity[]> = new EventEmitter<FileEntity[]>();

  public isExpanded: boolean = false;
  public showUploadForm: boolean = false;
  public isCollapsed: boolean = true;
  public isDragging: boolean = false;

  private onChange: Function = (files: FileEntity[]) => {};
  private onTouched: Function = () => {};

  constructor(private fileService: FileUploadService) { }

  writeValue(files: FileEntity[]): void {
    
    if (files) {
      // Replace the current attachments array with the new one
      // Filter out any FileUpload objects that might be in progress
      this.attachments = [...files].filter(file => !(file instanceof FileUpload));
      
    } else {
      this.attachments = [];
      
    }
  }

  registerOnChange(fn: Function): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  public toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  public openFileDialog(event: MouseEvent): void {
    // Stop propagation to prevent the header click from toggling collapse
    event.stopPropagation();
    
    // If collapsed, expand first
    if (this.isCollapsed) {
      this.isCollapsed = false;
    }
    
    // Open file dialog
    this.fileInput.nativeElement.click();
  }

  public selectFile(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.uploadFile(files[0]);
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    
    // If collapsed, expand first
    if (this.isCollapsed) {
      this.isCollapsed = false;
    }
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.uploadFile(files[0]);
    }
  }

  private uploadFile(file: File): void {
    if (file) {
      
      const fileUpload = new FileUpload(file);
      
      // Add the file upload to the attachments array immediately
      this.attachments.push(fileUpload);
      
      const uploadTask = this.fileService.pushFileToStorage(
        this.uploadFolder ? this.uploadFolder : 'attachments',
        fileUpload
      );

      uploadTask.percentageChanges().subscribe((value) => {
        const percentage = Math.round(value ? value : 0);
        
        
        // Update the percentage in the FileUpload object
        fileUpload.percentage = percentage;
        
        // Trigger change detection
        this.onChange([...this.attachments]);
      });

      uploadTask.then((value) => {
        value.ref.getDownloadURL().then((url) => {
          
          
          // Find the index of the FileUpload object in the attachments array
          const index = this.attachments.findIndex(item => 
            item instanceof FileUpload && item.tempId === fileUpload.tempId
          );
          
          if (index !== -1) {
            // Replace the FileUpload object with the URL
            this.attachments[index] = url;
            
            // Notify about the change
            this.onChange([...this.attachments]);
            this.changed.emit(this.attachments);
            this.fileAdded.emit(url);
          } else {
            // If for some reason the FileUpload object is not found, add the URL
            this.addFileToAttachments(url);
          }
          
          // Reset file input to allow selecting the same file again
          if (this.fileInput && this.fileInput.nativeElement) {
            this.fileInput.nativeElement.value = '';
          }
        });
      }).catch(error => {
        console.error('Error uploading file:', error);
        
        // Find and remove the failed upload from the attachments array
        const index = this.attachments.findIndex(item => 
          item instanceof FileUpload && item.tempId === fileUpload.tempId
        );
        
        if (index !== -1) {
          this.attachments.splice(index, 1);
          this.onChange([...this.attachments]);
        }
      });
    }
  }

  // Private method to add file to attachments without emitting events
  private addFileToAttachments(url: string): void {
    
    
    // Check if file already exists to prevent duplicates
    if (!this.attachments.includes(url)) {
      this.attachments.push(url);
      
      
      // Call the control value accessor onChange
      this.onChange(this.attachments);
      
      // Emit events
      this.changed.emit(this.attachments);
      this.fileAdded.emit(url);
    } else {
      
    }
  }

  // Public method for external calls
  public addFile(file: string): void {
    
    this.addFileToAttachments(file);
  }

  public toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  public isFileUpload(file: any): boolean {
    return file instanceof FileUpload;
  }

  public getFileUploadPercentage(file: any): number {
    return file instanceof FileUpload ? file.percentage : 0;
  }

  public getFileName(file: any): string {
    if (file instanceof FileUpload) {
      if (file.name) {
        return file.name;
      }
      
      if ('name' in file.file) {
        return file.file.name;
      }
      
      return 'Uploading file...';
    }
    
    return 'Uploading file...';
  }

  public getFileDetails(link: any): Observable<{
    image: boolean;
    icon: string;
    filename: string;
    extension: string;
    isUploading?: boolean;
    percentage?: number;
  }> {
    // If this is a FileUpload object, return upload details
    if (link instanceof FileUpload) {
      let filename = 'Uploading...';
      let extension = '';
      
      if (link.name) {
        filename = link.name;
        extension = link.name.split('.').pop()?.toLowerCase() || '';
      } else if ('name' in link.file) {
        filename = link.file.name;
        extension = link.file.name.split('.').pop()?.toLowerCase() || '';
      }
      
      // Determine icon based on file extension
      const imageExtensions = /(jpg|jpeg|png|gif|bmp|svg|webp)$/i;
      const isImage = imageExtensions.test(extension);
      
      // Define icon mappings
      const iconMappings: { [key: string]: string } = {
        pdf: 'picture_as_pdf',
        doc: 'description',
        docx: 'description',
        xls: 'description',
        xlsx: 'description',
        ppt: 'description',
        pptx: 'description',
        jpg: 'image',
        jpeg: 'image',
        png: 'image',
        gif: 'image',
        bmp: 'image',
        svg: 'image',
        webp: 'image',
        txt: 'description',
        rtf: 'description',
        csv: 'table_chart',
        json: 'code',
        xml: 'code',
        html: 'code',
        css: 'code',
        js: 'code',
        ts: 'code',
        zip: 'folder_zip',
        rar: 'folder_zip',
        tar: 'folder_zip',
        gz: 'folder_zip',
        '7z': 'folder_zip',
        mp3: 'music_note',
        wav: 'music_note',
        ogg: 'music_note',
        mp4: 'video_library',
        mov: 'video_library',
        avi: 'video_library',
        wmv: 'video_library',
        webm: 'video_library',
        default: 'insert_drive_file', // Default icon for other file types
      };
      
      const icon = iconMappings[extension] || iconMappings['default'];
      
      return of({
        image: false, // Don't show image preview during upload
        icon,
        filename,
        extension,
        isUploading: true,
        percentage: link.percentage
      });
    }
    
    // Regular URL processing for completed uploads
    try {
      // Parse the link URL to extract the file path
      const urlParts = new URL(link);

      // Use the pathname property to get the file path
      const filePath = decodeURIComponent(urlParts.pathname);

      // Extract the base file name and remove the file extension
      const pathnameParts = filePath.split('/');
      const fileNameWithExtension = pathnameParts[pathnameParts.length - 1];
      const fileNameWithoutExtension = fileNameWithExtension.replace(
        /\.[^/.]+$/,
        ''
      );

      // Get the file extension
      const fileExtension = fileNameWithExtension.split('.').pop();
      const cleanExtension = fileExtension ? fileExtension.toLowerCase() : '';

      // Check if it's an image based on the extension
      const imageExtensions = /(jpg|jpeg|png|gif|bmp|svg|webp)$/i;
      const isImage = imageExtensions.test(cleanExtension);

      // Define icon mappings
      const iconMappings: { [key: string]: string } = {
        pdf: 'picture_as_pdf',
        doc: 'description',
        docx: 'description',
        xls: 'description',
        xlsx: 'description',
        ppt: 'description',
        pptx: 'description',
        jpg: 'image',
        jpeg: 'image',
        png: 'image',
        gif: 'image',
        bmp: 'image',
        svg: 'image',
        webp: 'image',
        txt: 'description',
        rtf: 'description',
        csv: 'table_chart',
        json: 'code',
        xml: 'code',
        html: 'code',
        css: 'code',
        js: 'code',
        ts: 'code',
        zip: 'folder_zip',
        rar: 'folder_zip',
        tar: 'folder_zip',
        gz: 'folder_zip',
        '7z': 'folder_zip',
        mp3: 'music_note',
        wav: 'music_note',
        ogg: 'music_note',
        mp4: 'video_library',
        mov: 'video_library',
        avi: 'video_library',
        wmv: 'video_library',
        webm: 'video_library',
        default: 'insert_drive_file', // Default icon for other file types
      };

      // Get the icon name based on the file extension
      const icon = iconMappings[cleanExtension] || iconMappings['default'];

      return of({
        image: isImage,
        icon,
        filename: fileNameWithoutExtension,
        extension: cleanExtension,
        isUploading: false
      });
    } catch (error) {
      console.error(`Error parsing link: ${error}`);
      return of({
        image: false,
        icon: 'insert_drive_file',
        filename: '',
        extension: '',
        isUploading: false
      });
    }
  }

  public deleteFile(file: any) {
    try {
      // If it's a FileUpload object (in-progress upload), just remove it from the array
      if (file instanceof FileUpload) {
        const index = this.attachments.findIndex(item => 
          item instanceof FileUpload && item.tempId === file.tempId
        );
        
        if (index !== -1) {
          this.attachments.splice(index, 1);
          this.onChange(this.attachments);
          this.changed.emit(this.attachments);
        }
        return;
      }
      
      // Otherwise, it's a completed upload (URL)
      const decodedUrl = decodeURIComponent(file);
      const storagePath = decodedUrl.split('/o/')[1].split('?')[0];

      // Delete from storage
      this.fileService.deleteFileStorage(storagePath);
      
      // Find the file in the attachments array
      // First try to find by comparing the full URL
      let index = this.attachments.findIndex((attachment) => attachment === file);
      
      // If not found by direct comparison, try to find by name property if it exists
      if (index === -1 && file.name) {
        index = this.attachments.findIndex((attachment) => 
          attachment.name === file.name || 
          (typeof attachment === 'string' && attachment.includes(file.name))
        );
      }
      
      // If still not found, try to extract filename from URL and compare
      if (index === -1) {
        const fileName = decodedUrl.split('/').pop()?.split('?')[0];
        if (fileName) {
          index = this.attachments.findIndex((attachment) => {
            if (typeof attachment === 'string') {
              return attachment.includes(fileName);
            } else if (attachment.name) {
              return attachment.name.includes(fileName);
            }
            return false;
          });
        }
      }

      // Remove the file if found
      if (index !== -1) {
        this.attachments.splice(index, 1);
        this.onChange(this.attachments);
        this.changed.emit(this.attachments);
      } else {
        console.error('Could not find file to delete in attachments array', file);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  }
}
